import * as CL from '@design-system/component-library';
import { useState } from 'react';
import { dsClass } from '../../common/constants/dsClasses.js';
import { AddOnDescription } from '../SubscriptionDetails/AddOnDescription.js';
import type { BadgeProps } from '@design-system/component-library';
import type { ReactNode } from 'react';

import './ToggleCheckbox.scss';

export interface PriceInfo {
  value: ReactNode;
  additionalText?: ReactNode;
}

export interface ToggleCheckboxCoCoProps {
  addOnDescriptionGuid?: string;
  badge?: BadgeProps;
  children?: ReactNode;
  initialValue?: boolean;
  onChange: (checked: boolean) => void;
  priceInfo?: PriceInfo;
  title: string;
  additionalInformation?: string | ReactNode;
  disabled?: boolean;
  quantitySelector?: ReactNode;
  additionalContent?: ReactNode;
  /** When true, the quantity selector is stacked on top of the price instead of sitting in the middle. */
  quantityAbovePrice?: boolean;
}
// TODO: Replace custom price with DS price component.
export const Price = ({ badge, priceInfo }: { badge?: BadgeProps; priceInfo?: PriceInfo }) => {
  if (!priceInfo?.additionalText && !badge) {
    return priceInfo?.value ? (
      <div className={`${dsClass.FONT_WEIGHT_BOLD} of-toggle-checkbox__price__basic`}>{priceInfo.value}</div>
    ) : null;
  }
  return (
    <div className="of-toggle-checkbox__price__additional-info">
      {badge && (
        <div>
          <CL.Badge {...badge} />
        </div>
      )}
      {priceInfo?.value && <div className={dsClass.FONT_WEIGHT_BOLD}>{priceInfo?.value}</div>}
      {priceInfo?.additionalText && (
        <div className={`${dsClass.FONT_SIZE_SMALL} of-toggle-checkbox__price__additional-info__additional-text`}>
          {priceInfo?.additionalText}
        </div>
      )}
    </div>
  );
};

export const ToggleCheckboxCoCo = (props: ToggleCheckboxCoCoProps) => {
  const {
    additionalInformation,
    addOnDescriptionGuid,
    badge,
    initialValue = false,
    children,
    title,
    onChange,
    priceInfo,
    disabled = false,
    quantitySelector,
    additionalContent,
    quantityAbovePrice = false,
  } = props;
  const [checked, setChecked] = useState(initialValue);

  // Choose style class depending on how much stuff there is to render, so that the used space can be optimized
  // If a badge or additional text exists, more space is needed
  const containerClass =
    priceInfo?.additionalText || badge
      ? 'of-toggle-checkbox__container__additional-info'
      : 'of-toggle-checkbox__container__basic';

  const quantityField = quantitySelector && (
    <div className="of-toggle-checkbox__quantity-selector">{quantitySelector}</div>
  );

  return (
    <div
      className={`of-toggle-checkbox${quantitySelector ? ' of-toggle-checkbox--has-quantity' : ''}${
        quantitySelector && quantityAbovePrice ? ' of-toggle-checkbox--quantity-above-price' : ''
      } ${dsClass.PADDING_3}`}
    >
      <div className={containerClass}>
        <div className="of-toggle-checkbox__header">
          <CL.Checkbox
            checked={initialValue}
            onChange={e => {
              setChecked(e.target.checked);
              onChange(e.target.checked);
            }}
            label={title}
            disabled={disabled}
          />
          {additionalInformation && (
            <div className={`${dsClass.PADDING_LEFT_5} ${dsClass.FONT_SIZE_SMALL}`}>{additionalInformation}</div>
          )}
        </div>
        {quantityAbovePrice && quantitySelector ? (
          <div className="of-toggle-checkbox__price-with-quantity">
            {quantityField}
            <Price badge={badge} priceInfo={priceInfo} />
          </div>
        ) : (
          <>
            {quantityField}
            <Price badge={badge} priceInfo={priceInfo} />
          </>
        )}
      </div>

      {addOnDescriptionGuid && (
        <div className={`of-toggle-checkbox__content ${dsClass.PADDING_LEFT_5}`}>
          <AddOnDescription addOnGuidCode={addOnDescriptionGuid} />
        </div>
      )}

      <div
        className={`${
          checked && !disabled ? 'of-toggle-checkbox__content__open' : 'of-toggle-checkbox__content__closed'
        } of-toggle-checkbox__content ${dsClass.PADDING_LEFT_5}`}
      >
        {children}
      </div>
      <div className={`${dsClass.PADDING_LEFT_5}`}>{additionalContent}</div>
    </div>
  );
};
